import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import FileUploader from "../../components/admin/FileUploader";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const [s, setS] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => { setS(r.data || {}); setLoaded(true); });
  }, []);

  const update = (k, v) => setS({ ...s, [k]: v });
  const save = async () => {
    try {
      // Don't send password if untouched
      const payload = { ...s };
      delete payload.id; delete payload.smtp_password_set; delete payload.updated_at; delete payload._singleton;
      await api.put("/admin/settings", payload);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!loaded) return null;
  const social = s.social_links || {};

  return (
    <AdminLayout>
      <PageHeader title="Settings" subtitle="Site identity, contact, email and analytics configuration." />
      <div className="p-8 max-w-3xl">
        <Tabs defaultValue="general">
          <TabsList className="bg-[#F7F8F6] border border-[#E2E8F0]">
            <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
            <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email">Email (SMTP)</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="seo" data-testid="tab-seo">SEO defaults</TabsTrigger>
            <TabsTrigger value="pharma" data-testid="tab-pharma">Pharma trust</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
            {/* ─── Brand logo ─── */}
            <div className="space-y-3 pb-4 border-b border-[#E2E8F0]">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#4B5563] font-semibold">Brand logo (header & footer)</div>              <div className="flex items-start gap-6 flex-wrap">
                <div className="w-40 h-24 rounded-lg border border-[#E2E8F0] bg-[#0F172A] flex items-center justify-center overflow-hidden">
                  {s.logo_url ? (
                    <img src={s.logo_url.startsWith("http") ? s.logo_url : `${BACKEND}${s.logo_url}`} alt="Logo preview" className="max-h-full max-w-full object-contain p-2" data-testid="logo-preview" />
                  ) : (
                    <span className="text-xs text-white/40">No logo set</span>
                  )}
                </div>
                <div className="flex-1 min-w-[260px] space-y-3">
                  <FileUploader
                    kind="image"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    label="Upload new logo (PNG / JPG / WEBP / SVG)"
                    currentUrl={s.logo_url}
                    onUploaded={(url) => {
                      // Always set the canonical brand logo + mirror into
                      // SEO/JSON-LD slots so a single upload updates the
                      // header, footer, OG image and Google Organization card.
                      setS((cur) => ({
                        ...cur,
                        logo_url: url,
                        organization_logo_url: url,
                        default_og_image: url,
                      }));
                    }}
                    testId="logo-uploader"
                  />
                  <div className="space-y-2">
                    <Label>Logo URL (auto-filled after upload — editable)</Label>
                    <Input value={s.logo_url || ""} onChange={(e) => update("logo_url", e.target.value)} placeholder="/api/uploads/your-logo.png" data-testid="logo-url" />
                    <p className="text-xs text-[#4B5563]">Recommended: 600×200 px PNG/SVG with transparent background. Click <strong>Save</strong> at the bottom of this page to apply.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Favicon / browser tab icon ─── */}
            <div className="space-y-3 pb-4 border-b border-[#E2E8F0]">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#4B5563] font-semibold">Favicon · browser tab & bookmark icon</div>
              <div className="flex items-start gap-6 flex-wrap">
                <div className="flex flex-col items-center gap-1">
                  <div className="w-16 h-16 rounded-lg border border-[#E2E8F0] bg-white flex items-center justify-center overflow-hidden" title="32×32 favicon preview">
                    {(s.favicon_url || s.logo_url) ? (
                      <img src={(s.favicon_url || s.logo_url).startsWith("http") ? (s.favicon_url || s.logo_url) : `${BACKEND}${s.favicon_url || s.logo_url}`} alt="Favicon preview" className="w-8 h-8 object-contain" data-testid="favicon-preview" />
                    ) : (
                      <span className="text-[10px] text-[#4B5563]">32×32</span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#4B5563]">Browser tab</div>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-20 h-20 rounded-2xl border border-[#E2E8F0] bg-[#0F172A] flex items-center justify-center overflow-hidden" title="180×180 apple-touch-icon preview">
                    {(s.apple_touch_icon_url || s.favicon_url || s.logo_url) ? (
                      <img src={(s.apple_touch_icon_url || s.favicon_url || s.logo_url).startsWith("http") ? (s.apple_touch_icon_url || s.favicon_url || s.logo_url) : `${BACKEND}${s.apple_touch_icon_url || s.favicon_url || s.logo_url}`} alt="Apple touch preview" className="w-14 h-14 object-contain" data-testid="apple-touch-preview" />
                    ) : (
                      <span className="text-[10px] text-white/60">180×180</span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#4B5563]">iOS home-screen</div>
                </div>
                <div className="flex-1 min-w-[260px] space-y-3">
                  <FileUploader
                    kind="image"
                    accept="image/png,image/x-icon,image/svg+xml,image/jpeg,image/webp"
                    label="Upload favicon (PNG / ICO / SVG — 32×32+ recommended)"
                    currentUrl={s.favicon_url}
                    onUploaded={(url) => {
                      // A single square icon is normally enough — mirror it
                      // into apple_touch_icon_url unless the admin has already
                      // set a dedicated higher-res file.
                      setS((cur) => ({
                        ...cur,
                        favicon_url: url,
                        apple_touch_icon_url: cur.apple_touch_icon_url || url,
                      }));
                    }}
                    testId="favicon-uploader"
                  />
                  <div className="space-y-2">
                    <Label>Favicon URL</Label>
                    <Input value={s.favicon_url || ""} onChange={(e) => update("favicon_url", e.target.value)} placeholder="/api/uploads/favicon.png" data-testid="favicon-url" />
                  </div>
                  <div className="space-y-2">
                    <Label>Apple touch-icon URL (180×180 PNG, optional)</Label>
                    <Input value={s.apple_touch_icon_url || ""} onChange={(e) => update("apple_touch_icon_url", e.target.value)} placeholder="/api/uploads/apple-touch-180.png" data-testid="apple-touch-url" />
                    <p className="text-xs text-[#4B5563]">Leave blank to reuse the favicon. Click <strong>Save</strong>, then hard-refresh (Ctrl/⌘+Shift+R) — browsers cache favicons aggressively.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2"><Label>Site tagline</Label><Input value={s.site_tagline || ""} onChange={(e) => update("site_tagline", e.target.value)} data-testid="site-tagline" /></div>
            <div className="space-y-2">
              <Label>Site base URL (canonical)</Label>
              <Input value={s.site_base_url || ""} onChange={(e) => update("site_base_url", e.target.value)} placeholder="https://bonheurpharma.in" data-testid="site-base-url" />
              <p className="text-xs text-[#4B5563]">Used to build absolute canonical / OG / sitemap URLs. Set this before going live.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Facebook URL</Label><Input value={social.facebook || ""} onChange={(e) => update("social_links", { ...social, facebook: e.target.value })} data-testid="soc-facebook" /></div>
              <div className="space-y-2"><Label>LinkedIn URL</Label><Input value={social.linkedin || ""} onChange={(e) => update("social_links", { ...social, linkedin: e.target.value })} data-testid="soc-linkedin" /></div>
              <div className="space-y-2"><Label>Twitter/X URL</Label><Input value={social.twitter || ""} onChange={(e) => update("social_links", { ...social, twitter: e.target.value })} data-testid="soc-twitter" /></div>
              <div className="space-y-2"><Label>Instagram URL</Label><Input value={social.instagram || ""} onChange={(e) => update("social_links", { ...social, instagram: e.target.value })} data-testid="soc-instagram" /></div>
              <div className="space-y-2"><Label>YouTube URL</Label><Input value={social.youtube || ""} onChange={(e) => update("social_links", { ...social, youtube: e.target.value })} placeholder="https://youtube.com/@bonheurpharma" data-testid="soc-youtube" /></div>
            </div>
            <div className="space-y-2 pt-2 border-t border-[#E2E8F0]">
              <Label>Footer newsletter / sign-off text</Label>
              <Textarea rows={2} value={s.newsletter_text || ""} onChange={(e) => update("newsletter_text", e.target.value)} placeholder="Trusted by clinicians and distributors across India." data-testid="newsletter-text" />
              <p className="text-xs text-[#4B5563]">Optional one-liner shown below the social icons in the footer.</p>
            </div>
          </TabsContent>

          <TabsContent value="contact" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
            <div className="space-y-2"><Label>Contact email</Label><Input value={s.contact_email || ""} onChange={(e) => update("contact_email", e.target.value)} data-testid="contact-email" /></div>
            <div className="space-y-2"><Label>Contact phone</Label><Input value={s.contact_phone || ""} onChange={(e) => update("contact_phone", e.target.value)} data-testid="contact-phone" /></div>
            <div className="space-y-2"><Label>Address</Label><Textarea rows={3} value={s.contact_address || ""} onChange={(e) => update("contact_address", e.target.value)} data-testid="contact-address" /></div>
            <div className="space-y-2"><Label>Google Maps embed (iframe URL)</Label><Textarea rows={2} value={s.map_embed || ""} onChange={(e) => update("map_embed", e.target.value)} placeholder="https://www.google.com/maps/embed?…" data-testid="map-embed" /></div>
          </TabsContent>

          <TabsContent value="email" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
            <p className="text-sm text-[#4B5563]">For Gmail SMTP, use host <code>smtp.gmail.com</code>, port <code>587</code> (TLS) or <code>465</code> (SSL), and an <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noreferrer" className="text-[#C2410C] underline">app password</a>.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>SMTP host</Label><Input value={s.smtp_host || ""} onChange={(e) => update("smtp_host", e.target.value)} placeholder="smtp.gmail.com" data-testid="smtp-host" /></div>
              <div className="space-y-2"><Label>Port</Label><Input type="number" value={s.smtp_port || ""} onChange={(e) => update("smtp_port", parseInt(e.target.value || 0))} placeholder="587" data-testid="smtp-port" /></div>
              <div className="space-y-2"><Label>Username</Label><Input value={s.smtp_user || ""} onChange={(e) => update("smtp_user", e.target.value)} data-testid="smtp-user" /></div>
              <div className="space-y-2">
                <Label>Password {s.smtp_password_set && <span className="text-xs text-[#4B5563]">(saved · leave blank to keep)</span>}</Label>
                <Input type="password" value={s.smtp_password || ""} onChange={(e) => update("smtp_password", e.target.value)} data-testid="smtp-password" />
              </div>
              <div className="space-y-2 col-span-2"><Label>From address</Label><Input value={s.smtp_from || ""} onChange={(e) => update("smtp_from", e.target.value)} placeholder="Bonheur Pharma <noreply@bonheurpharma.in>" data-testid="smtp-from" /></div>
              <div className="space-y-2 col-span-2"><Label>Notify (comma-separated emails)</Label><Input value={s.notify_emails || ""} onChange={(e) => update("notify_emails", e.target.value)} placeholder="ops@bonheurpharma.in, hr@…" data-testid="notify-emails" /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={s.smtp_use_tls !== false} onCheckedChange={(v) => update("smtp_use_tls", v)} data-testid="smtp-tls" />
              <Label>Use STARTTLS (recommended for port 587)</Label>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
            <div className="space-y-2">
              <Label>Google Analytics 4 — Measurement ID</Label>
              <Input value={s.ga_measurement_id || ""} onChange={(e) => update("ga_measurement_id", e.target.value)} placeholder="G-XXXXXXXXXX" data-testid="ga-id" />
              <p className="text-xs text-[#4B5563]">The frontend will inject the GA script when this is set.</p>
            </div>
          </TabsContent>

          <TabsContent value="seo" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#4B5563] font-semibold">Site verification</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-2"><Label>Google Search Console</Label><Input value={s.google_site_verification || ""} onChange={(e) => update("google_site_verification", e.target.value)} placeholder="verification token" data-testid="google-verify" /></div>
              <div className="space-y-2"><Label>Bing Webmaster</Label><Input value={s.bing_site_verification || ""} onChange={(e) => update("bing_site_verification", e.target.value)} data-testid="bing-verify" /></div>
              <div className="space-y-2"><Label>Facebook domain</Label><Input value={s.fb_domain_verification || ""} onChange={(e) => update("fb_domain_verification", e.target.value)} data-testid="fb-verify" /></div>
            </div>

            <div className="pt-4 border-t border-[#E2E8F0] text-[10px] tracking-[0.3em] uppercase text-[#4B5563] font-semibold">Open Graph / Social share defaults</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-2 md:col-span-3">
                <FileUploader
                  kind="image"
                  accept="image/png,image/jpeg,image/webp"
                  label="Upload default OG image (1200×630 recommended)"
                  currentUrl={s.default_og_image}
                  onUploaded={(url) => update("default_og_image", url)}
                  testId="og-uploader"
                />
              </div>
              <div className="space-y-2"><Label>Default OG image URL</Label><Input value={s.default_og_image || ""} onChange={(e) => update("default_og_image", e.target.value)} placeholder="/api/uploads/…" data-testid="default-og" /></div>
              <div className="space-y-2"><Label>OG site name</Label><Input value={s.og_site_name || ""} onChange={(e) => update("og_site_name", e.target.value)} placeholder="Bonheur Pharma" data-testid="og-site-name" /></div>
              <div className="space-y-2"><Label>Twitter handle</Label><Input value={s.twitter_handle || ""} onChange={(e) => update("twitter_handle", e.target.value)} placeholder="@bonheurpharma" data-testid="twitter-handle" /></div>
            </div>

            <div className="pt-4 border-t border-[#E2E8F0]">
              <Label>Custom robots.txt (optional)</Label>
              <Textarea rows={6} value={s.robots_txt || ""} onChange={(e) => update("robots_txt", e.target.value)} placeholder="Leave blank to auto-generate (recommended)." data-testid="robots-txt" />
              <p className="text-xs text-[#4B5563] mt-1">Served at <code>/api/robots.txt</code>. Auto-generated robots includes sitemap pointer when base URL is set.</p>
            </div>
            <p className="text-xs text-[#4B5563]">Per-page SEO meta is managed under the SEO page in the sidebar. Per-entity SEO (products, blogs, etc.) is on each item's edit dialog.</p>
          </TabsContent>

          <TabsContent value="pharma" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#4B5563] font-semibold">Organization (for JSON-LD)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Legal/registered name</Label><Input value={s.organization_legal_name || ""} onChange={(e) => update("organization_legal_name", e.target.value)} placeholder="Bonheur Pharma Pvt. Ltd." data-testid="org-name" /></div>
              <div className="space-y-2">
                <Label>Organization logo URL (for JSON-LD)</Label>
                <Input value={s.organization_logo_url || ""} onChange={(e) => update("organization_logo_url", e.target.value)} placeholder="/api/uploads/…" data-testid="org-logo" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <FileUploader
                  kind="image"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  label="Upload organization logo (used in Google JSON-LD)"
                  currentUrl={s.organization_logo_url}
                  onUploaded={(url) => update("organization_logo_url", url)}
                  testId="org-logo-uploader"
                />
              </div>
            </div>

            <div className="pt-4 border-t border-[#E2E8F0]">
              <Label>Global medical disclaimer</Label>
              <Textarea rows={4} value={s.global_medical_disclaimer || ""} onChange={(e) => update("global_medical_disclaimer", e.target.value)} placeholder="The information on this website is for general informational purposes only. Always consult a qualified healthcare professional…" data-testid="medical-disclaimer" />
              <p className="text-xs text-[#4B5563] mt-1">Shown in the footer and on product/blog pages (Phase 2 frontend). Strongly recommended for pharma (YMYL) sites.</p>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-6">
          <Button onClick={save} data-testid="save-settings" className="bg-[#C2410C] hover:bg-[#9A3412]">Save all settings</Button>
        </div>
      </div>
    </AdminLayout>
  );
}
